
import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
  Home, 
  Settings, 
  Users, 
  Building, 
  Image, 
  Calendar,
  User,
  BarChart3,
  MessageSquare,
  Star
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DashboardNavItemsProps {
  activeTab: string;
  nursingHomeSlug?: string;
}

export function DashboardNavItems({ activeTab, nursingHomeSlug }: DashboardNavItemsProps) {
  const basePath = nursingHomeSlug ? `/owner/nursing-homes/${nursingHomeSlug}` : '';

  // Fetch unread message count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-messages", nursingHomeSlug],
    queryFn: async () => {
      if (!nursingHomeSlug) return 0;
      const { count, error } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("nursing_home_id", nursingHomeSlug)
        .eq("is_read", false)
        .eq("sender_type", "family");
      if (error) return 0;
      return count || 0;
    },
    enabled: !!nursingHomeSlug,
    refetchInterval: 30000,
  });

  const navItems = [
    {
      label: "Overview",
      icon: Home,
      href: `${basePath}/`,
      id: "overview",
    },
    {
      label: "Profile",
      icon: Building,
      href: `${basePath}/profile`,
      id: "profile",
    },
    {
      label: "Media",
      icon: Image,
      href: `${basePath}/media`,
      id: "media",
    },
    {
      label: "Leads",
      icon: Users,
      href: `${basePath}/leads`,
      id: "leads",
    },
    {
      label: "Messages",
      icon: MessageSquare,
      href: `${basePath}/messages`,
      id: "messages",
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
    {
      label: "Reviews",
      icon: Star,
      href: `${basePath}/reviews`,
      id: "reviews",
    },
    {
      label: "Analytics",
      icon: BarChart3,
      href: `${basePath}/analytics`,
      id: "analytics",
    },
    {
      label: "Events",
      icon: Calendar,
      href: `${basePath}/events`,
      id: "events",
    },
    {
      label: "Staff",
      icon: User,
      href: `${basePath}/staff`,
      id: "staff",
    },
    {
      label: "Settings",
      icon: Settings,
      href: `${basePath}/settings`,
      id: "settings",
    },
  ];
  
  return (
    <div className="p-2">
      <nav className="flex flex-col gap-1">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          
          return (
            <Link
              key={item.id}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                isActive 
                  ? "bg-grandpal-green text-white font-medium" 
                  : "hover:bg-gray-100"
              )}
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
